/**
 * notificacionesHelper.js
 * 
 * Helper para emitir notificaciones al superadministrador desde cualquier módulo.
 * Uso: importar y llamar notificar({ modulo, accion, titulo, detalle })
 * 
 * Ejemplo:
 *   import { notificar } from '@/utils/notificacionesHelper';
 *   notificar({ modulo: 'venta', accion: 'Creada', titulo: 'Venta #123', detalle: 'Cliente: Juan Pérez' });
 */
import { agregarNotificacion } from './notificaciones';

/**
 * Emite una notificación al panel del superadministrador.
 * @param {Object} opciones
 * @param {string} opciones.modulo   - Módulo (ej: 'venta', 'cliente', 'producto')
 * @param {string} opciones.accion   - Acción (ej: 'Creado', 'Editado', 'Eliminado', 'Aprobado')
 * @param {string} opciones.titulo   - Título corto de la notificación
 * @param {string} [opciones.detalle] - Detalle adicional (opcional)
 */
export function notificar({ modulo, accion, titulo, detalle = '' }) {
  try {
    agregarNotificacion({ modulo, accion, titulo, detalle });
  } catch (e) {
    // Silenciar errores para no afectar el flujo principal
    console.warn('[Notificaciones] Error al registrar notificación:', e);
  }
}

// ==================== Funciones de conveniencia por módulo ====================

export const notificarVenta = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'venta', accion, titulo, detalle });

export const notificarCliente = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'cliente', accion, titulo, detalle });

export const notificarProducto = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'producto', accion, titulo, detalle });

export const notificarRecepcion = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'recepcion', accion, titulo, detalle });

export const notificarRecepcionExpress = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'recepcionExpress', accion, titulo, detalle });

export const notificarPago = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'pago', accion, titulo, detalle });

export const notificarUsuario = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'usuario', accion, titulo, detalle });

export const notificarRol = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'rol', accion, titulo, detalle });

export const notificarProveedor = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'proveedor', accion, titulo, detalle });

export const notificarCompra = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'compra', accion, titulo, detalle });

export const notificarCredito = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'credito', accion, titulo, detalle });

export const notificarServicio = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'servicio', accion, titulo, detalle });

export const notificarCategoria = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'categoria', accion, titulo, detalle });

export const notificarTecnico = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'tecnico', accion, titulo, detalle });

export const notificarHerramienta = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'herramienta', accion, titulo, detalle });

export const notificarCaja = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'caja', accion, titulo, detalle });

export const notificarEstado = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'estado', accion, titulo, detalle });

export const notificarGarantia = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'garantia', accion, titulo, detalle });

export const notificarTasa = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'tasa', accion, titulo, detalle });

export const notificarSede = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'sede', accion, titulo, detalle });

export const notificarVendedor = (accion, titulo, detalle = '') =>
  notificar({ modulo: 'vendedor', accion, titulo, detalle });
